import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useMemo,
	useState,
} from "react";

import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";

const MutesContext = createContext({
	isMuted: () => false,
	mutedUntil: () => undefined,
	mute: async () => {},
	unmute: async () => {},
});

// contactId -> Date | null (null = sempre)
const toEntry = until => (until ? new Date(until) : null);

const isActive = entry => entry === null || (entry instanceof Date && entry.getTime() > Date.now());

const MutesProvider = ({ children }) => {
	const { user, socket } = useContext(AuthContext);
	const userId = user?.id;
	const [mutes, setMutes] = useState({});
	// ids tocados por evento/ação antes do snapshot do GET chegar: esses vencem
	const touched = useRef(new Set());

	useEffect(() => {
		if (!userId) return undefined;
		let cancelled = false;
		touched.current = new Set();
		api
			.get("/mutes")
			.then(({ data }) => {
				if (cancelled || !Array.isArray(data)) return;
				setMutes(prev => {
					// nada novo: mantém a identidade (sem re-render extra após o carregamento)
					if (data.length === 0 && touched.current.size === 0 && Object.keys(prev).length === 0) return prev;
					const next = {};
					data.forEach(m => {
						if (!touched.current.has(String(m.contactId))) next[m.contactId] = toEntry(m.until);
					});
					touched.current.forEach(id => {
						if (id in prev) next[id] = prev[id];
					});
					return next;
				});
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [userId]);

	useEffect(() => {
		if (!userId || !socket) return undefined;
		const event = `user-${userId}-mute`;
		const onMute = data => {
			if (!data || data.contactId == null) return;
			touched.current.add(String(data.contactId));
			setMutes(prev => {
				const next = { ...prev };
				if (data.action === "remove") delete next[data.contactId];
				else next[data.contactId] = toEntry(data.until);
				return next;
			});
		};
		socket.on(event, onMute);
		return () => socket.off(event, onMute);
	}, [userId, socket]);

	// um único timeout para o próximo vencimento; sem silêncio com prazo, nenhum timer
	useEffect(() => {
		const dates = Object.values(mutes).filter(e => e instanceof Date);
		if (dates.length === 0) return undefined;
		const earliest = Math.min(...dates.map(d => d.getTime()));
		const delay = Math.min(Math.max(earliest - Date.now(), 0) + 50, 2147483000);
		const id = setTimeout(() => {
			setMutes(prev => {
				const next = {};
				let changed = false;
				Object.keys(prev).forEach(k => {
					if (isActive(prev[k])) next[k] = prev[k];
					else changed = true;
				});
				return changed ? next : prev;
			});
		}, delay);
		return () => clearTimeout(id);
	}, [mutes]);

	const mutedUntil = useCallback(
		contactId => {
			const entry = mutes[contactId];
			return entry === undefined || isActive(entry) ? entry : undefined;
		},
		[mutes]
	);

	const isMuted = useCallback(
		contactId => {
			if (contactId == null) return false;
			const entry = mutes[contactId];
			return entry !== undefined && isActive(entry);
		},
		[mutes]
	);

	const mute = useCallback(async (contactId, duration) => {
		const { data } = await api.post("/mutes", { contactId, duration });
		touched.current.add(String(contactId));
		setMutes(prev => ({ ...prev, [contactId]: toEntry(data?.until) }));
		return data;
	}, []);

	const unmute = useCallback(async contactId => {
		await api.delete(`/mutes/${contactId}`);
		touched.current.add(String(contactId));
		setMutes(prev => {
			const next = { ...prev };
			delete next[contactId];
			return next;
		});
	}, []);

	const value = useMemo(
		() => ({ isMuted, mutedUntil, mute, unmute }),
		[isMuted, mutedUntil, mute, unmute]
	);

	return <MutesContext.Provider value={value}>{children}</MutesContext.Provider>;
};

const useMutes = () => useContext(MutesContext);

export { MutesContext, MutesProvider, useMutes };
